// CSV-Import der Eigentümer als reine Funktionen (Handoff Eigentümer §6.2).
// Übernommen aus EigentuemerCsvImportFormular: Auto-Mapping, Name-Split (Firma, Paare,
// „Nachname, Vorname“), Adress-Split (Straße, Nr., PLZ, Stadt), Telefon mit Vorwahl,
// Dubletten (vollständigster Eintrag bleibt), Namensprüfung und Objekt-Erkennung über
// Dateiname bzw. Anschrift. Neu: Anrede aus „Herr/Frau“, Schalter für die Optionen,
// E-Mail-Prüfung und Abgleich mit vorhandenen Eigentümern schon in der Vorschau.

export type ImportFeld =
  | 'anrede' | 'vorname' | 'nachname' | 'firma_name' | 'strasse' | 'hausnummer' | 'plz' | 'stadt'
  | 'email' | 'telefon' | 'mobil' | 'bankverbindung' | 'steuer_id' | 'notizen' | '_skip';

export type Umwandlung = 'name_split' | 'adresse_split' | 'telefon_combine';

export interface Spaltenzuordnung {
  csvSpalte: string;
  feld: ImportFeld;
  aktiv: boolean;
  umwandlung?: Umwandlung;
  /** automatisch erkannt (Chip „Auto“) */
  auto: boolean;
}

export const IMPORT_FELDER: readonly { key: ImportFeld; label: string }[] = [
  { key: 'anrede', label: 'Anrede' },
  { key: 'vorname', label: 'Vorname' },
  { key: 'nachname', label: 'Nachname' },
  { key: 'firma_name', label: 'Firma' },
  { key: 'strasse', label: 'Straße' },
  { key: 'hausnummer', label: 'Hausnummer' },
  { key: 'plz', label: 'PLZ' },
  { key: 'stadt', label: 'Stadt' },
  { key: 'email', label: 'E-Mail' },
  { key: 'telefon', label: 'Telefon' },
  { key: 'mobil', label: 'Mobil' },
  { key: 'bankverbindung', label: 'Bankverbindung (IBAN)' },
  { key: 'steuer_id', label: 'Steuer-ID' },
  { key: 'notizen', label: 'Notizen' },
  { key: '_skip', label: '– nicht importieren –' },
];

const AUTO_MAP: Record<string, ImportFeld | Umwandlung> = {
  name: 'name_split', kontaktname: 'name_split',
  vorname: 'vorname', nachname: 'nachname',
  briefanrede: 'anrede', anrede: 'anrede',
  firma: 'firma_name', firma_name: 'firma_name', firmenname: 'firma_name',
  adresse: 'adresse_split', anschrift: 'adresse_split', kontaktadresse: 'adresse_split',
  'straße': 'strasse', strasse: 'strasse', hausnummer: 'hausnummer',
  plz: 'plz', postleitzahl: 'plz',
  stadt: 'stadt', ort: 'stadt', city: 'stadt',
  'e-mail': 'email', email: 'email', mail: 'email', 'kontakte-mail': 'email',
  telefon: 'telefon_combine', telefonnummer: 'telefon_combine', tel: 'telefon_combine',
  phone: 'telefon', kontakttelefon: 'telefon',
  mobil: 'mobil', handy: 'mobil', mobile: 'mobil',
  bankverbindung: 'bankverbindung', iban: 'bankverbindung',
  steuer_id: 'steuer_id', steuernummer: 'steuer_id',
  notizen: 'notizen', bemerkung: 'notizen',
};

const SKIP_SPALTEN = ['id', 'benutzername', 'staat', 'land', 'landesvorwahl', 'vorwahl', 'country', 'state'];

const istUmwandlung = (x: ImportFeld | Umwandlung): x is Umwandlung => x === 'name_split' || x === 'adresse_split' || x === 'telefon_combine';

/**
 * Trennzeichen aus der Kopfzeile: das häufigste von Semikolon, Tab, Komma (außerhalb von
 * Anführungszeichen). Früher galten alle drei gleichzeitig – „Uferstraße 9, 35037 Marburg“
 * in einer Semikolon-Datei wurde dadurch in zwei Spalten zerrissen.
 */
export function erkenneTrennzeichen(kopfzeile: string): ';' | '\t' | ',' {
  const anzahl = { ';': 0, '\t': 0, ',': 0 };
  let inAnf = false;
  for (const ch of kopfzeile) {
    if (ch === '"') inAnf = !inAnf;
    else if (!inAnf && (ch === ';' || ch === '\t' || ch === ',')) anzahl[ch]++;
  }
  if (anzahl[';'] >= anzahl['\t'] && anzahl[';'] >= anzahl[','] && anzahl[';'] > 0) return ';';
  return anzahl['\t'] >= anzahl[','] && anzahl['\t'] > 0 ? '\t' : ',';
}

/** Zerlegt CSV-Text; Trennzeichen automatisch (Semikolon, Tab oder Komma), Anführungszeichen mit "" escaped. */
export function parseCsv(text: string): { kopf: string[]; zeilen: string[][] } {
  const zeilen = text.replace(/^﻿/, '').split(/\r?\n/).filter(l => l.trim());
  if (zeilen.length < 2) return { kopf: [], zeilen: [] };
  const trenner = erkenneTrennzeichen(zeilen[0]);
  const zerlege = (zeile: string): string[] => {
    const teile: string[] = [];
    let aktuell = '';
    let inAnf = false;
    for (let i = 0; i < zeile.length; i++) {
      const ch = zeile[i];
      if (ch === '"') {
        if (inAnf && zeile[i + 1] === '"') { aktuell += '"'; i++; } else inAnf = !inAnf;
      } else if (ch === trenner && !inAnf) {
        teile.push(aktuell.trim());
        aktuell = '';
      } else aktuell += ch;
    }
    teile.push(aktuell.trim());
    return teile;
  };
  return { kopf: zerlege(zeilen[0]), zeilen: zeilen.slice(1).map(zerlege).filter(r => r.some(c => c)) };
}

/** UTF-8 falsch gelesen (Ersatzzeichen oder „Ã¤“-Muster) → mit Windows-1252 erneut lesen. */
export function brauchtWindows1252(text: string): boolean {
  return text.includes('�') || /Ã¶|Ã¤|Ã¼|Ã–|Ã„|Ãœ|ÃŸ/.test(text);
}

const spaltenSchluessel = (h: string): string => h.toLowerCase().replace(/[^a-zäöüß_-]/g, '').trim();

/** Auto-Mapping je Kopfspalte; die Spalte „Vorwahl“ wird für die Telefonnummer gemerkt. */
export function autoZuordnung(kopf: readonly string[]): { zuordnungen: Spaltenzuordnung[]; vorwahlSpalte: number } {
  let vorwahlSpalte = -1;
  const zuordnungen = kopf.map((h, i): Spaltenzuordnung => {
    const key = spaltenSchluessel(h);
    if (SKIP_SPALTEN.includes(key)) {
      if (key === 'vorwahl') vorwahlSpalte = i;
      return { csvSpalte: h, feld: '_skip', aktiv: false, auto: false };
    }
    const ziel = AUTO_MAP[key];
    if (!ziel) return { csvSpalte: h, feld: '_skip', aktiv: false, auto: false };
    if (istUmwandlung(ziel)) {
      const feld: ImportFeld = ziel === 'name_split' ? 'nachname' : ziel === 'adresse_split' ? 'strasse' : 'telefon';
      return { csvSpalte: h, feld, aktiv: true, umwandlung: ziel, auto: true };
    }
    return { csvSpalte: h, feld: ziel, aktiv: true, auto: true };
  });
  return { zuordnungen, vorwahlSpalte };
}

/** Zielbeschreibung für die Mapping-Liste: „Anrede, Vorname, Nachname, Firma“. */
export function zielText(z: Spaltenzuordnung, optionen: Pick<ImportOptionen, 'nameSplit' | 'adressSplit'>): string {
  if (!z.aktiv || z.feld === '_skip') return '– nicht importieren –';
  if (z.umwandlung === 'name_split') return optionen.nameSplit ? 'Anrede, Vorname, Nachname, Firma' : 'Nachname';
  if (z.umwandlung === 'adresse_split') return optionen.adressSplit ? 'Straße, Nr., PLZ, Stadt' : 'Straße';
  if (z.umwandlung === 'telefon_combine') return 'Telefon (mit Vorwahl)';
  return IMPORT_FELDER.find(f => f.key === z.feld)?.label ?? z.feld;
}

export interface NameTeil {
  anrede: '' | 'Herr' | 'Frau' | 'Firma';
  vorname: string;
  nachname: string;
  firma: string;
  /** Reihenfolge unklar („Schwede, Björn“) – zur Prüfung vorlegen */
  pruefen: boolean;
  original: string;
}

const FIRMEN_MERKMALE = ['gmbh', 'gbr', 'kg', 'ohg', 'ag', 'e.k.', 'co kg', 'ug', 'e.v.', 'e. v.', 'ow-re', 'immobilien', 'versicherung', 'gemeinde',
  'kirche', 'stiftung', 'verein', 'genossenschaft', 'stadtwerke', 'sparkasse', 'volksbank', 'diakonie', 'caritas', 'co.kg', 'mbh', 'inc', 'ltd', 'llc'];

/** Firmenmerkmal als ganzes Wort (sonst wäre „Agnes“ wegen „ag“ eine Firma). */
function istFirmenname(text: string): boolean {
  const klein = text.toLowerCase();
  return FIRMEN_MERKMALE.some(m => {
    const muster = m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[\\s,&(+-])${muster}($|[\\s,.)&+-])`).test(klein);
  });
}

const person = (anrede: NameTeil['anrede'], vorname: string, nachname: string, original: string, pruefen = false): NameTeil =>
  ({ anrede, vorname, nachname, firma: '', pruefen, original });

/** Teilt einen Namen in Personen bzw. eine Firma (Logik der bisherigen Import-Maske). */
export function teileName(voll: string): NameTeil[] {
  const sauber = (voll ?? '').replace(/,\s*$/, '').trim();
  if (!sauber) return [person('', '', '', '')];

  if (istFirmenname(sauber)) return [{ anrede: 'Firma', vorname: '', nachname: '', firma: sauber, pruefen: false, original: sauber }];

  const woerter = sauber.split(/\s+/);
  if (woerter.length >= 4) {
    const typisch = /^(Herr|Frau|Dr\.|Prof\.)/i.test(sauber)
      || (woerter.length <= 5 && /^[A-ZÄÖÜ][a-zäöüß]+$/.test(woerter[0]) && /^[A-ZÄÖÜ][a-zäöüß]+$/.test(woerter[woerter.length - 1]));
    if (!typisch && !sauber.includes(' und ') && !sauber.includes(' & ')) {
      return [{ anrede: 'Firma', vorname: '', nachname: '', firma: sauber, pruefen: false, original: sauber }];
    }
  }

  // „Frau … Herr …“: Paar mit Anreden
  const titel = [...sauber.matchAll(/\b(Herr|Frau)\b/gi)];
  if (titel.length >= 2) {
    const ergebnis: NameTeil[] = [];
    titel.forEach((t, i) => {
      const start = (t.index ?? 0) + t[0].length;
      const ende = i + 1 < titel.length ? (titel[i + 1].index ?? sauber.length) : sauber.length;
      const teile = sauber.substring(start, ende).replace(/\b(und|&)\s*$/i, '').trim().split(/\s+/).filter(Boolean);
      const anrede = t[1].toLowerCase() === 'herr' ? 'Herr' : 'Frau';
      if (teile.length >= 2) ergebnis.push(person(anrede, teile.slice(0, -1).join(' '), teile[teile.length - 1], sauber));
      else if (teile.length === 1) ergebnis.push(person(anrede, teile[0], '', sauber));
    });
    // gemeinsamer Nachname: „Frau Anna und Herr Paul Berg“
    const letzterNachname = ergebnis[ergebnis.length - 1]?.nachname ?? '';
    return ergebnis.map(p => (p.nachname ? p : { ...p, nachname: letzterNachname }));
  }

  const hatKomma = /,\s*[A-ZÄÖÜ]/.test(sauber);
  const hatUnd = sauber.includes(' und ') || sauber.includes(' & ');

  if (hatKomma && !hatUnd) {
    const teile = sauber.split(/,\s*/);
    if (teile.length === 2) {
      const a = teile[0].trim().split(/\s+/);
      const b = teile[1].trim().split(/\s+/);
      // zwei volle Namen: „Sven Achenbach, Laura Niggemann“
      if (a.length >= 2 && b.length >= 2) {
        return [person('', a.slice(0, -1).join(' '), a[a.length - 1], sauber), person('', b.slice(0, -1).join(' '), b[b.length - 1], sauber)];
      }
      // „Nachname, Vorname“ → zur Prüfung
      return [person('', teile[1].trim(), teile[0].trim(), sauber, true)];
    }
  }

  if (hatUnd) {
    const alle = sauber.split(/,\s*/).flatMap(t => t.split(/\s+(?:und|&)\s+/)).map(t => t.trim()).filter(Boolean);
    const letzte = (alle[alle.length - 1] ?? '').split(/\s+/);
    const gemeinsam = letzte.length >= 2 ? letzte[letzte.length - 1] : '';
    return alle.map(p => {
      const t = p.split(/\s+/);
      if (t.length >= 2) return person('', t.slice(0, -1).join(' '), t[t.length - 1], sauber);
      if (t.length === 1 && gemeinsam) return person('', t[0], gemeinsam, sauber);
      return person('', '', t[0] ?? '', sauber);
    });
  }

  const anredeTreffer = /^(Herr|Frau)\s+/i.exec(sauber);
  const anrede: NameTeil['anrede'] = anredeTreffer ? (anredeTreffer[1].toLowerCase() === 'herr' ? 'Herr' : 'Frau') : '';
  const name = sauber
    .replace(/^(Herr\s+|Frau\s+)/i, '')
    .replace(/^(Dr\.\s*|Prof\.\s*|Dipl\.\s*-?\w+\s*)+/i, '')
    .replace(/\s*geb\.\s*\S+/i, '')
    .trim();
  const teile = name.split(/\s+/).filter(Boolean);
  if (teile.length <= 1) return [person(anrede, '', teile[0] ?? '', sauber)];
  return [person(anrede, teile.slice(0, -1).join(' '), teile[teile.length - 1], sauber)];
}

/** „Uferstraße 9a“ → Straße + Nr. (auch „12-14“). */
export function teileAdresse(adresse: string): { strasse: string; hausnummer: string } {
  const a = (adresse ?? '').trim();
  if (!a) return { strasse: '', hausnummer: '' };
  const m = /^(.+?)\s+(\d+\s*[a-zA-Z]?(?:\s*[-–]\s*\d+\s*[a-zA-Z]?)?)$/.exec(a);
  return m ? { strasse: m[1].trim(), hausnummer: m[2].trim() } : { strasse: a, hausnummer: '' };
}

/** „Dr.-Born-Str. 43, 34537 Bad Wildungen“ → Straße, Nr., PLZ, Stadt. */
export function teileAnschrift(wert: string): { strasse: string; hausnummer: string; plz: string; stadt: string } {
  const v = (wert ?? '').trim();
  const m = /,?\s*(\d{5})\s+(.+)$/.exec(v);
  const rest = m ? v.substring(0, m.index).replace(/,\s*$/, '').trim() : v;
  return { ...teileAdresse(rest), plz: m ? m[1] : '', stadt: m ? m[2].trim() : '' };
}

export interface ImportOptionen {
  nameSplit: boolean;
  tauschen: boolean;
  adressSplit: boolean;
  objektAuto: boolean;
}
export const STANDARD_OPTIONEN: ImportOptionen = { nameSplit: true, tauschen: false, adressSplit: true, objektAuto: true };

export interface ImportZeile {
  anrede: string;
  vorname: string;
  nachname: string;
  firma_name: string;
  strasse: string;
  hausnummer: string;
  plz: string;
  stadt: string;
  email: string;
  telefon: string;
  mobil: string;
  bankverbindung: string;
  steuer_id: string;
  notizen: string;
  pruefenName: boolean;
  originalName: string;
}

const leereZeile = (): ImportZeile => ({
  anrede: '', vorname: '', nachname: '', firma_name: '', strasse: '', hausnummer: '', plz: '', stadt: '',
  email: '', telefon: '', mobil: '', bankverbindung: '', steuer_id: '', notizen: '', pruefenName: false, originalName: '',
});

/** Baut je CSV-Zeile einen oder mehrere Datensätze (Paare werden getrennt). */
export function baueImportZeilen(daten: readonly string[][], zuordnungen: readonly Spaltenzuordnung[], vorwahlSpalte: number, optionen: ImportOptionen): ImportZeile[] {
  const alle: ImportZeile[] = [];
  for (const roh of daten) {
    const basis = leereZeile();
    let personen: NameTeil[] = [];
    zuordnungen.forEach((z, i) => {
      if (!z.aktiv || z.feld === '_skip') return;
      const wert = (roh[i] ?? '').trim();
      if (z.umwandlung === 'name_split') {
        personen = optionen.nameSplit ? teileName(wert) : [person('', '', wert, wert)];
      } else if (z.umwandlung === 'adresse_split') {
        if (optionen.adressSplit) Object.assign(basis, teileAnschrift(wert));
        else basis.strasse = wert;
      } else if (z.umwandlung === 'telefon_combine') {
        const vorwahl = vorwahlSpalte >= 0 ? (roh[vorwahlSpalte] ?? '').trim() : '';
        if (wert) basis.telefon = vorwahl ? `0${vorwahl.replace(/^0/, '')} ${wert}` : wert;
      } else {
        basis[z.feld] = wert;
      }
    });
    if (personen.length === 0) personen = [person('', '', '', '')];
    const gemeinsamerNachname = basis.nachname;
    for (const p of personen) {
      const z: ImportZeile = { ...basis };
      if (p.firma) {
        z.firma_name = p.firma;
        z.vorname = '';
        z.nachname = '';
        z.anrede = 'Firma';
      } else {
        z.vorname = p.vorname || basis.vorname;
        z.nachname = (p.nachname || gemeinsamerNachname).replace(/,\s*$/, '').trim();
        if (p.anrede) z.anrede = p.anrede;
        if (optionen.tauschen) [z.vorname, z.nachname] = [z.nachname, z.vorname];
      }
      z.pruefenName = p.pruefen;
      z.originalName = p.original;
      if (z.nachname || z.firma_name || z.vorname) alle.push(z);
    }
  }
  return alle;
}

const UNGUELTIGE_BEGRIFFE = ['eigentümergemeinschaft', 'eigentümerschaft', 'hausverwaltung', 'verwaltung', 'gemeinschaft', 'erbengemeinschaft',
  'unbekannt', 'mieter', 'vermieter', 'bewohner', 'inhaber', 'weg '];

/** Namensprüfung der bisherigen Maske: kein Name, Sammelbegriff, zu kurz. */
export function namensFehler(z: Pick<ImportZeile, 'vorname' | 'nachname' | 'firma_name'>): string | null {
  const vorname = z.vorname.trim();
  const nachname = z.nachname.trim();
  const firma = z.firma_name.trim();
  if (!vorname && !nachname && !firma) return 'Kein Name vorhanden';
  if (nachname && UNGUELTIGE_BEGRIFFE.some(t => nachname.toLowerCase().includes(t))) return `„${nachname}“ ist kein gültiger Name`;
  if (vorname && UNGUELTIGE_BEGRIFFE.some(t => vorname.toLowerCase().includes(t))) return `„${vorname}“ ist kein gültiger Name`;
  if (!vorname && !firma && nachname.length <= 2) return `Nachname „${nachname}“ zu kurz`;
  return null;
}

export const emailGueltig = (s: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());

const vollstaendigkeit = (z: ImportZeile): number =>
  (z.hausnummer ? 2 : 0) + (z.email ? 2 : 0) + (z.telefon ? 2 : 0) + (z.mobil ? 1 : 0) + (z.strasse ? 1 : 0) + (z.plz ? 1 : 0) + (z.stadt ? 1 : 0);

const namensSchluessel = (z: Pick<ImportZeile, 'vorname' | 'nachname' | 'firma_name'>): string =>
  `${z.vorname.trim().toLowerCase()}|${z.nachname.trim().toLowerCase()}|${z.firma_name.trim().toLowerCase()}`;

export interface ObjektKurz {
  id: string;
  bezeichnung: string | null;
  strasse: string | null;
  hausnummer: string | null;
  stadt: string | null;
  objektnummer?: string | null;
}

/** Objekt aus dem Dateinamen „Eigentümer_0041 WEG Pfarracker 2, MR.csv“ (Nummer, Bezeichnung, Straße). */
export function objektAusDateiname(dateiname: string, objekte: readonly ObjektKurz[]): ObjektKurz | null {
  const name = dateiname.replace(/\.(csv|txt|tsv|xlsx|xls)$/i, '').trim();
  const nummer = /(\d{3,})/.exec(name)?.[1] ?? '';
  const text = name.replace(/^[^0-9]*\d*\s*/i, '').trim().toLowerCase();
  if (!text && !nummer) return null;
  if (nummer) {
    const t = objekte.find(o => (o.objektnummer ?? '').includes(nummer));
    if (t) return t;
  }
  for (const o of objekte) {
    const bez = (o.bezeichnung ?? '').toLowerCase();
    if (bez.length > 3 && text.includes(bez)) return o;
  }
  for (const o of objekte) {
    const str = (o.strasse ?? '').toLowerCase();
    const nr = (o.hausnummer ?? '').toLowerCase();
    if (str.length > 3 && text.includes(str) && (!nr || text.includes(nr))) return o;
  }
  for (const o of objekte) {
    const bez = (o.bezeichnung ?? '').toLowerCase();
    if (bez && text.length > 5 && bez.includes(text.substring(0, 20))) return o;
  }
  return null;
}

const strassenNorm = (s: string): string => s.toLowerCase().replace(/straße|strasse|str\./g, 'str').replace(/\s+/g, '');

/** Objekt über die Anschrift (Straße gleich, Hausnummer enthalten, Stadt passend). */
export function objektAusAdresse(z: Pick<ImportZeile, 'strasse' | 'hausnummer' | 'stadt'>, objekte: readonly ObjektKurz[]): ObjektKurz | null {
  if (!z.strasse) return null;
  const str = strassenNorm(z.strasse);
  const nr = z.hausnummer.replace(/\s/g, '').toLowerCase();
  if (!nr) return null;
  for (const o of objekte) {
    const oNr = (o.hausnummer ?? '').replace(/\s/g, '').toLowerCase();
    if (str !== strassenNorm(o.strasse ?? '') || !oNr) continue;
    if (nr !== oNr && !oNr.includes(nr) && !nr.includes(oNr)) continue;
    const stadt = z.stadt.toLowerCase();
    const oStadt = (o.stadt ?? '').toLowerCase();
    if (!stadt || !oStadt || stadt.includes(oStadt) || oStadt.includes(stadt)) return o;
  }
  return null;
}

export interface BestehenderEig {
  id: string;
  vorname?: string | null;
  nachname?: string | null;
  firma_name?: string | null;
  email?: string | null;
}

/** Vorhandener Eigentümer: gleiche E-Mail, bei Firma gleicher Firmenname, sonst gleicher Nach- (und Vor-)name. */
export function findeVorhanden(z: Pick<ImportZeile, 'vorname' | 'nachname' | 'firma_name' | 'email'>, bestehende: readonly BestehenderEig[]): BestehenderEig | null {
  const mail = z.email.trim().toLowerCase();
  if (mail) {
    const t = bestehende.find(b => (b.email ?? '').trim().toLowerCase() === mail);
    if (t) return t;
  }
  const gleich = (a: string | null | undefined, b: string) => (a ?? '').trim().toLowerCase() === b.trim().toLowerCase();
  if (z.firma_name && !z.nachname) return bestehende.find(b => gleich(b.firma_name, z.firma_name)) ?? null;
  if (!z.nachname) return null;
  return bestehende.find(b => gleich(b.nachname, z.nachname) && (!z.vorname || gleich(b.vorname, z.vorname))) ?? null;
}

export type VorschauStatus = 'gueltig' | 'pruefen' | 'vorhanden' | 'dublette' | 'fehler';

export interface VorschauZeile {
  index: number;
  zeile: ImportZeile;
  status: VorschauStatus;
  hinweise: string[];
  objekt: ObjektKurz | null;
  /** vorhandener Eigentümer – wird nicht neu angelegt, nur dem Dateiobjekt zugeordnet */
  vorhandenId: string | null;
  /** standardmäßig ausgeschlossen (Dublette, ungültiger Name) */
  standardAus: boolean;
}

export interface VorschauKontext {
  bestehende: readonly BestehenderEig[];
  objekte: readonly ObjektKurz[];
  dateiObjekt: ObjektKurz | null;
  objektAuto: boolean;
}

/** Vorschau mit Status je Zeile: gültig · prüfen · vorhanden · Dublette · Fehler. */
export function baueVorschau(zeilen: readonly ImportZeile[], k: VorschauKontext): VorschauZeile[] {
  // Dubletten in der Datei: gleicher Name (vollständigster Eintrag bleibt) oder gleiche E-Mail (erster bleibt)
  const gruppen = new Map<string, number[]>();
  zeilen.forEach((z, i) => {
    const key = namensSchluessel(z);
    if (key !== '||') gruppen.set(key, [...(gruppen.get(key) ?? []), i]);
  });
  const dublette = new Map<number, string>();
  for (const idx of gruppen.values()) {
    if (idx.length < 2) continue;
    const bester = idx.reduce((b, i) => (vollstaendigkeit(zeilen[i]) > vollstaendigkeit(zeilen[b]) ? i : b), idx[0]);
    for (const i of idx) if (i !== bester) dublette.set(i, `Dublette (vollständiger in Zeile ${bester + 1})`);
  }
  const ersteMail = new Map<string, number>();
  zeilen.forEach((z, i) => {
    const mail = z.email.trim().toLowerCase();
    if (!mail || dublette.has(i)) return;
    const erste = ersteMail.get(mail);
    if (erste === undefined) ersteMail.set(mail, i);
    else dublette.set(i, `Dublette (gleiche E-Mail wie Zeile ${erste + 1})`);
  });

  return zeilen.map((zeile, index): VorschauZeile => {
    const fehler = namensFehler(zeile);
    const vorhanden = fehler ? null : findeVorhanden(zeile, k.bestehende);
    // vorhandene Eigentümer erhalten nur das Objekt aus dem Dateinamen (wie bisher)
    const objekt = !k.objektAuto ? null : vorhanden ? k.dateiObjekt : (k.dateiObjekt ?? objektAusAdresse(zeile, k.objekte));
    const basis = { index, zeile, objekt, vorhandenId: vorhanden?.id ?? null };
    if (fehler) return { ...basis, status: 'fehler', hinweise: [fehler], standardAus: true };
    const dub = dublette.get(index);
    if (dub) return { ...basis, status: 'dublette', hinweise: [dub], standardAus: true };
    if (vorhanden) return { ...basis, status: 'vorhanden', hinweise: [objekt ? 'existiert bereits · wird dem Objekt zugeordnet' : 'existiert bereits'], standardAus: false };
    const hinweise: string[] = [];
    if (zeile.pruefenName) hinweise.push('Vor-/Nachname prüfen');
    if (!zeile.email) hinweise.push('E-Mail fehlt');
    else if (!emailGueltig(zeile.email)) hinweise.push('E-Mail ungültig');
    return { ...basis, status: hinweise.length ? 'pruefen' : 'gueltig', hinweise, standardAus: false };
  });
}

/** Anzeigename einer Importzeile (Firma bzw. „Frau Monika Seidel“). */
export function importName(z: Pick<ImportZeile, 'anrede' | 'vorname' | 'nachname' | 'firma_name'>): string {
  if (z.firma_name && !z.nachname) return z.firma_name;
  const anrede = z.anrede === 'Herr' || z.anrede === 'Frau' ? z.anrede : '';
  return [anrede, z.vorname, z.nachname].filter(Boolean).join(' ') || z.firma_name || '–';
}

/** Spalten der Vorlage (Semikolon, UTF-8 mit BOM). */
export const VORLAGE_KOPF = ['Anrede', 'Vorname', 'Nachname', 'Firma', 'Straße', 'Hausnummer', 'PLZ', 'Stadt', 'E-Mail', 'Telefon', 'Mobil', 'IBAN', 'Steuer-ID', 'Notizen'];

export function vorlageCsv(): string {
  return `﻿${VORLAGE_KOPF.join(';')}\r\n`;
}

/** Datensatz für `eigentuemer` (Firma → Anrede „Firma“, lib/eigentuemer/anzeige.ts). */
export function eigentuemerDatensatz(z: ImportZeile): Record<string, string> {
  const firma = z.firma_name.trim();
  const istFirma = z.anrede === 'Firma' || (!!firma && !z.vorname.trim() && !z.nachname.trim());
  return {
    anrede: istFirma ? 'Firma' : z.anrede,
    vorname: z.vorname.trim(),
    nachname: z.nachname.trim(),
    firma_name: firma,
    strasse: z.strasse.trim(),
    hausnummer: z.hausnummer.trim(),
    plz: z.plz.trim(),
    stadt: z.stadt.trim(),
    email: z.email.trim(),
    telefon: z.telefon.trim(),
    mobil: z.mobil.trim(),
    bankverbindung: z.bankverbindung.replace(/\s+/g, ' ').trim(),
    steuer_id: z.steuer_id.trim(),
    notizen: z.notizen.trim(),
    status: 'Aktiv',
  };
}
